import { useState, useEffect } from 'react';
import { getResearchArticles, createResearchArticle, updateResearchArticle, deleteResearchArticle, ResearchArticle } from '../lib/supabase';

export function useSupabaseResearch() {
  const [articles, setArticles] = useState<ResearchArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const data = await getResearchArticles();
      setArticles(data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading research articles:', err);
      setError(err.message);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArticles();
  }, []);

  const addArticle = async (articleData: Omit<ResearchArticle, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const created = await createResearchArticle(articleData);
      await loadArticles();
      return created;
    } catch (err: any) {
      console.error('Error adding research article:', err);
      throw err;
    }
  };

  const updateArticle = async (id: string, articleData: Partial<ResearchArticle>) => {
    try {
      const updated = await updateResearchArticle(id, articleData);
      await loadArticles();
      return updated;
    } catch (err: any) {
      console.error('Error updating research article:', err);
      throw err;
    }
  };

  const deleteArticle = async (id: string) => {
    try {
      await deleteResearchArticle(id);
      await loadArticles();
    } catch (err: any) {
      console.error('Error deleting research article:', err);
      throw err;
    }
  };

  return {
    articles,
    loading,
    error,
    addArticle,
    updateArticle,
    deleteArticle,
    refreshArticles: loadArticles
  };
}
